/**
 * Slice 19c — reusable chip-strip multi-select for archetype tag defs.
 * Used by the library Templates create form (and any future template editor)
 * to choose which archetype tags to persist into ads_assistant_template_tag_links.
 */
import type { AdsAssistantPostTagDefRow } from "@/lib/adsAssistantClient";

export const MAX_TEMPLATE_ARCHETYPE_TAGS = 8;

export function AdsArchetypeTagMultiSelect(props: {
  /** All tag defs from the org (any kind); component filters to archetype-kind. */
  tagDefs: AdsAssistantPostTagDefRow[];
  /** Currently selected archetype tag ids. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Optional id to disambiguate when multiple instances render on a page. */
  idPrefix?: string;
}) {
  const { tagDefs, value, onChange, idPrefix = "archetype-multiselect" } = props;
  const archetypes = tagDefs.filter((t) => t.kind === "archetype");
  const selected = new Set(value);

  if (!archetypes.length) {
    return (
      <p className="text-[11px] text-gray-500 dark:text-gray-400">
        No archetype tags yet — add them under Post tags first to attach formats to templates.
      </p>
    );
  }

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      if (next.size >= MAX_TEMPLATE_ARCHETYPE_TAGS) return;
      next.add(id);
    }
    onChange([...next]);
  };

  return (
    <div>
      <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300">
        Archetype tags
        <span className="ml-1 text-gray-400">
          ({value.length}/{MAX_TEMPLATE_ARCHETYPE_TAGS})
        </span>
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        {archetypes.map((t) => {
          const isSelected = selected.has(t.id);
          return (
            <button
              key={t.id}
              type="button"
              id={`${idPrefix}-${t.id}`}
              onClick={() => toggle(t.id)}
              aria-pressed={isSelected}
              className={
                "rounded-full px-2 py-0.5 text-[11px] font-medium transition " +
                (isSelected
                  ? "bg-blue-700 text-white hover:bg-blue-800"
                  : "bg-blue-50 text-blue-800 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-200 dark:hover:bg-blue-900/50")
              }
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Read-only chip strip for archetype tag ids — used to display saved
 * archetype tags inline on each template row in the library list.
 */
export function AdsArchetypeTagChipStrip(props: {
  tagDefs: AdsAssistantPostTagDefRow[];
  value: string[];
}) {
  const { tagDefs, value } = props;
  if (!value.length) return null;
  const byId = new Map(tagDefs.map((t) => [t.id, t] as const));
  const chips = value
    .map((id) => byId.get(id))
    .filter((t): t is AdsAssistantPostTagDefRow => !!t);
  if (!chips.length) return null;
  return (
    <p className="mt-0.5 flex flex-wrap gap-1">
      {chips.map((t) => (
        <span
          key={t.id}
          className="rounded-full bg-blue-50 px-1.5 py-px text-[10px] font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
        >
          {t.label}
        </span>
      ))}
    </p>
  );
}
